import React, { useState, useRef } from 'react';
import { 
  FileType, Upload, Download, Copy, Check, 
  AlertCircle, FileText, ArrowLeft, RefreshCw, 
  Sparkles, Eye, Code, FileCode, CheckCircle2, ShieldCheck
} from 'lucide-react';
import { LocalModelToggle } from './LocalModelToggle';

interface MdToDocxConverterProps {
  language: 'uk' | 'en' | 'pl';
  onBack: () => void;
  onGoHome?: () => void;
  useEnterpriseContext?: boolean;
  enterpriseName?: string;
}

const DEMO_MARKDOWN_UK = `# НАКАЗ
## Про затвердження Положення про використання цифрових інструментів у діловодстві підприємства

**Дата видачі:** 31 липня 2026 року  
**Номер наказу:** № 142-ОД  
**Місто:** м. Київ  

---

### 1. ОБҐРУНТУВАННЯ ТА МЕТА
З метою підвищення ефективності підготовки службової документації, прискорення аналітичної обробки матеріалів та дотримання вимог Національного стандарту **ДСТУ 4163:2020** в організації,

### 2. НАКАЗУЮ:
1. Затвердити **Положення про автоматизовану обробку документів** через систему *Deep Research Hub*.
2. Керівникам структурних підрозділів забезпечити використання сумісних форматів даних:

| № п/п | Тип документа | Формат зауважень | Стандарт оформлення |
| :---: | :--- | :---: | :--- |
| **1** | Аналітичні звіти | Markdown (.md) | ДСТУ 4163:2020 |
| **2** | Розпорядчі накази | Word (.docx) | Times New Roman, 14pt, 1.5 |
| **3** | Фінансові розрахунки | Excel (.xlsx) | ПКМУ № 55 |

> **Примітка:** Усі створені документи підлягають автоматичній верифікації на дотримання полів 30/10/20/20 мм та абзацного відступу 12.5 мм.

---

### 3. КОНТРОЛЬ ВИКОНАННЯ
Контроль за виконанням даного наказу залишаю за собою.

**Директор ТОВ "ІННОВАЦІЙНІ ТЕХНОЛОГІЇ"**  
*(підпис)* **Олександр КОВАЛЕНКО**

---
*Звіт та документ згенеровано у системі Deep Research Hub (https://research.ai-assistent.online)*
`;

const DEMO_MARKDOWN_EN = `# OFFICIAL ORDER
## On Approval of Guidelines for AI Agent Usage in Corporate Documentation

**Issue Date:** July 31, 2026  
**Order Number:** # 142-OD  
**Location:** Kyiv  

---

### 1. RATIONALE AND PURPOSE
To increase efficiency in processing corporate files and ensure strict compliance with Ukrainian National Standard **DSTU 4163:2020**,

### 2. IT IS HEREBY ORDERED:
1. Approve the **Regulations on Automated Document Processing** via *Deep Research Hub*.
2. Department heads shall ensure usage of compatible data formats:

| # | Document Type | Source Format | Target Standard |
| :-: | :--- | :-: | :--- |
| **1** | Analytical Reports | Markdown (.md) | DSTU 4163:2020 |
| **2** | Executive Orders | Word (.docx) | Times New Roman, 14pt, 1.5 |
| **3** | Financial Audits | Excel (.xlsx) | Resolution #55 |

> **Note:** All created Word documents must strictly enforce margins (30/10/20/20 mm) and paragraph indents (12.5 mm).

---

**Director of Innovation Tech LLC**  
*(signature)* **Oleksandr KOVALENKO**
`;

export default function MdToDocxConverter({ language, onBack, onGoHome, useEnterpriseContext, enterpriseName }: MdToDocxConverterProps) {
  const isUk = language === 'uk';
  const [markdownInput, setMarkdownInput] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileSize, setFileSize] = useState<number | null>(null);

  const [aiEnhance, setAiEnhance] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [useLocalModel, setUseLocalModel] = useState<boolean>(() => {
    const saved = localStorage.getItem('prefer_local_model');
    return saved !== null ? saved === 'true' : true;
  });
  const [error, setError] = useState<string | null>(null);

  const [convertedMarkdown, setConvertedMarkdown] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'preview' | 'code'>('preview');
  const [copiedCode, setCopiedCode] = useState(false);
  const [downloadingFormat, setDownloadingFormat] = useState<'docx' | 'pdf' | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Strip ```markdown code fences
  const cleanCodeFences = (str: string): string => {
    if (!str) return '';
    let cleaned = str.trim();
    if (cleaned.startsWith('```')) {
      cleaned = cleaned.replace(/^```[a-zA-Z]*\n?/, '').replace(/\n?```$/, '');
    }
    return cleaned.trim();
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.md') && !file.name.endsWith('.txt') && !file.name.endsWith('.markdown')) {
      setError(isUk ? 'Будь ласка, оберіть файл формату .md або .txt' : 'Please select a .md or .txt file');
      return;
    }

    setFileName(file.name);
    setFileSize(file.size);
    setError(null);

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      setMarkdownInput(content || '');
    };
    reader.readAsText(file);
  };

  const handleRunConversion = async () => {
    if (!markdownInput.trim()) return;

    setIsProcessing(true);
    setError(null);

    try {
      if (aiEnhance) {
        // Call AI to refine layout, clean formatting and ensure DSTU structure
        const customKey = localStorage.getItem('custom_gemini_api_key');
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (customKey) headers['x-api-key'] = customKey;

        const response = await fetch('/api/run-templated-crew', {
          method: 'POST',
          headers,
          body: JSON.stringify({
            crewName: "ШІ-Оптимізатор та Конвертер Markdown за ДСТУ 4163:2020",
            agentId: "md-docx-converter",
            useLocalModel,
            inputs: {
              raw_details: markdownInput,
              document_purpose: "Конвертація Markdown у структурований Word-документ за ДСТУ 4163:2020"
            },
            useEnterpriseContext
          })
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.error || (isUk ? 'Помилка ШІ-обробки файлу.' : 'AI processing error.'));
        }

        const data = await response.json();
        // If async job returned
        if (data.jobId) {
          let pollAttempts = 0;
          const maxAttempts = 60;
          while (pollAttempts < maxAttempts) {
            await new Promise((res) => setTimeout(res, 2000));
            const statusRes = await fetch(`/api/crew-status/${data.jobId}`);
            if (statusRes.ok) {
              const statusData = await statusRes.json();
              if (statusData.status === 'completed') {
                setConvertedMarkdown(cleanCodeFences(statusData.report || markdownInput));
                break;
              } else if (statusData.status === 'failed') {
                throw new Error(statusData.error || (isUk ? 'Помилка виконання.' : 'Execution failed.'));
              }
            }
            pollAttempts++;
          }
        } else if (data.report) {
          setConvertedMarkdown(cleanCodeFences(data.report));
        } else {
          setConvertedMarkdown(markdownInput);
        }
      } else {
        // Direct conversion without AI step
        setConvertedMarkdown(markdownInput);
      }
    } catch (err: any) {
      console.error(err);
      setError(err.message || (isUk ? 'Сталася помилка.' : 'An error occurred.'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownloadWord = async () => {
    const textToConvert = convertedMarkdown || markdownInput;
    if (!textToConvert.trim()) return;

    setDownloadingFormat('docx');
    try {
      const baseName = fileName ? fileName.replace(/\.[^/.]+$/, "") : "document";
      const name = `${baseName}_dstu_4163`;
      
      const response = await fetch('/api/generate-docx', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: textToConvert,
          fileName: name
        })
      });

      if (!response.ok) throw new Error(isUk ? 'Не вдалося згенерувати файл Word.' : 'Failed to generate Word document.');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${name}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error(err);
      alert(err.message);
    } finally {
      setDownloadingFormat(null);
    }
  };

  const handleDownloadPdf = async () => {
    const textToConvert = convertedMarkdown || markdownInput;
    if (!textToConvert.trim()) return;

    setDownloadingFormat('pdf');
    try {
      const baseName = fileName ? fileName.replace(/\.[^/.]+$/, "") : "document";
      const name = `${baseName}_dstu_4163`;

      const response = await fetch('/api/generate-pdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: textToConvert,
          fileName: name
        })
      });

      if (!response.ok) throw new Error(isUk ? 'Не вдалося згенерувати PDF файл.' : 'Failed to generate PDF document.');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${name}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error(err);
      alert(err.message);
    } finally {
      setDownloadingFormat(null);
    }
  };

  const handleCopy = () => {
    const textToCopy = convertedMarkdown || markdownInput;
    if (!textToCopy) return;
    navigator.clipboard.writeText(textToCopy);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  // Helper: Inline markdown formatter
  const renderInline = (str: string): React.ReactNode => {
    if (!str) return null;
    const parts: React.ReactNode[] = [];
    const regex = /(\*\*(.*?)\*\*|\*(.*?)\*|`(.*?)`)/g;
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(str)) !== null) {
      if (match.index > lastIndex) {
        parts.push(str.substring(lastIndex, match.index));
      }
      if (match[2] !== undefined) {
        parts.push(<strong key={match.index} className="font-bold text-slate-900">{match[2]}</strong>);
      } else if (match[3] !== undefined) {
        parts.push(<em key={match.index} className="italic text-slate-800">{match[3]}</em>);
      } else if (match[4] !== undefined) {
        parts.push(<code key={match.index} className="px-1 py-0.5 bg-slate-100 border border-slate-200 rounded text-indigo-700 font-mono text-xs">{match[4]}</code>);
      }
      lastIndex = regex.lastIndex;
    }
    if (lastIndex < str.length) {
      parts.push(str.substring(lastIndex));
    }
    return parts.length > 0 ? parts : str;
  };

  // DSTU Document Renderer Simulation
  const renderDstuPreview = (mdText: string) => {
    const lines = mdText.split('\n');
    const elements: React.ReactNode[] = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];
      const trimmed = line.trim();

      // Heading 1
      if (trimmed.startsWith('# ')) {
        elements.push(
          <h1 key={i} className="text-xl font-bold uppercase tracking-wider text-center text-slate-900 my-4 font-serif">
            {trimmed.replace(/^#\s+/, '')}
          </h1>
        );
        i++;
        continue;
      }

      // Heading 2
      if (trimmed.startsWith('## ')) {
        elements.push(
          <h2 key={i} className="text-base font-bold text-slate-900 my-3 text-center font-serif leading-snug">
            {trimmed.replace(/^##\s+/, '')}
          </h2>
        );
        i++;
        continue;
      }

      // Heading 3
      if (trimmed.startsWith('### ')) {
        elements.push(
          <h3 key={i} className="text-sm font-bold uppercase text-slate-800 mt-4 mb-2 font-serif">
            {trimmed.replace(/^###\s+/, '')}
          </h3>
        );
        i++;
        continue;
      }

      // Horizontal Rule
      if (trimmed === '---' || trimmed === '***' || trimmed === '___') {
        elements.push(<hr key={i} className="border-t border-slate-300 my-4" />);
        i++;
        continue;
      }

      // Table
      if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
        const tableLines: string[] = [];
        while (i < lines.length && lines[i].trim().startsWith('|') && lines[i].trim().endsWith('|')) {
          tableLines.push(lines[i].trim());
          i++;
        }
        if (tableLines.length >= 2) {
          const parseRow = (row: string) => row.replace(/^\||\|$/g, '').split('|').map(c => c.trim());
          const headers = parseRow(tableLines[0]);
          const isSeparator = tableLines[1].includes('---');
          const bodyRows = isSeparator ? tableLines.slice(2) : tableLines.slice(1);

          elements.push(
            <div key={i} className="my-4 overflow-x-auto border border-slate-400 rounded-xs">
              <table className="w-full text-xs text-left border-collapse font-serif">
                <thead>
                  <tr className="bg-slate-100 border-b border-slate-400">
                    {headers.map((h, idx) => (
                      <th key={idx} className="p-2 border.r border-slate-300 font-bold text-slate-800">{renderInline(h)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {bodyRows.map((r, rIdx) => (
                    <tr key={rIdx} className="border-b border-slate-300 hover:bg-slate-50">
                      {parseRow(r).map((cell, cIdx) => (
                        <td key={cIdx} className="p-2 border-r border-slate-300 text-slate-700">{renderInline(cell)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
          continue;
        }
      }

      // Blockquote
      if (trimmed.startsWith('>')) {
        elements.push(
          <blockquote key={i} className="pl-4 border-l-4 border-indigo-500 italic text-slate-700 my-3 font-serif bg-slate-50/50 p-2 text-xs">
            {renderInline(trimmed.replace(/^>\s*/, ''))}
          </blockquote>
        );
        i++;
        continue;
      }

      // Bullet List
      if (trimmed.startsWith('* ') || trimmed.startsWith('- ')) {
        elements.push(
          <li key={i} className="ml-6 list-disc text-xs text-slate-800 font-serif leading-relaxed my-1">
            {renderInline(trimmed.replace(/^[\*\-]\s+/, ''))}
          </li>
        );
        i++;
        continue;
      }

      // Empty line
      if (!trimmed) {
        elements.push(<div key={i} className="h-2" />);
        i++;
        continue;
      }

      // Regular Paragraph (DSTU Style: indent 12.5mm / 1.5em)
      elements.push(
        <p key={i} className="text-xs text-slate-800 font-serif leading-relaxed text-justify indent-6 my-1">
          {renderInline(line)}
        </p>
      );
      i++;
    }

    return elements;
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto px-4 pb-12 text-slate-800">
      {/* Top Header */}
      <div className="flex items-center justify-between border-b border-slate-200 pb-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <button
              onClick={onGoHome || onBack}
              className="px-3.5 py-2 text-xs font-mono font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded transition-all cursor-pointer uppercase flex items-center gap-1.5"
            >
              🏠 {isUk ? "На головну" : "Home"}
            </button>
            <button
              onClick={onBack}
              className="px-3.5 py-2 text-xs font-mono font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded transition-all cursor-pointer uppercase flex items-center gap-1.5"
            >
              <ArrowLeft className="w-4 h-4 text-blue-600" />
              <span>{isUk ? "← До Офісного Центру" : "← Back to Office Hub"}</span>
            </button>
          </div>
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <FileType className="w-6 h-6 text-indigo-600" />
              <span>{isUk ? "Конвертер Markdown у Word (ДСТУ 4163)" : "Markdown to Word (DSTU 4163) Converter"}</span>
            </h1>
            <p className="text-xs text-slate-500">
              {isUk 
                ? "Миттєва конвертація .md документів у формат Microsoft Word (.docx) за ДСТУ 4163:2020 з підтримкою ШІ-форматування" 
                : "Instant conversion of .md files into Microsoft Word (.docx) strictly following DSTU 4163:2020 standard"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <LocalModelToggle
            isLocal={useLocalModel}
            onToggle={(enabled) => {
              setUseLocalModel(enabled);
              localStorage.setItem('prefer_local_model', enabled ? 'true' : 'false');
            }}
            language={language}
          />
        </div>
      </div>

      {/* Enterprise Context Notification */}
      {useEnterpriseContext && (
        <div className="bg-indigo-50/70 border border-indigo-200 rounded-xl p-3.5 flex items-center justify-between text-xs text-indigo-900">
          <div className="flex items-center gap-2.5">
            <ShieldCheck className="w-4 h-4 text-indigo-600" />
            <div>
              <span className="font-bold">{isUk ? "Контекст підприємства: " : "Enterprise Context: "}</span>
              <span className="font-mono">{enterpriseName || (isUk ? "База знань компанії" : "Company KB")}</span>
            </div>
          </div>
          <span className="text-[10px] px-2 py-0.5 bg-indigo-200/50 rounded text-indigo-800 font-bold uppercase tracking-wider">
            {isUk ? "ДСТУ Налаштування" : "DSTU Preset"}
          </span>
        </div>
      )}

      {/* Input Section */}
      {!convertedMarkdown && !isProcessing && (
        <div className="space-y-4">
          {/* Quick Demo Button */}
          <button
            type="button"
            onClick={() => {
              setMarkdownInput(isUk ? DEMO_MARKDOWN_UK : DEMO_MARKDOWN_EN);
              setFileName('order_sample_dstu.md');
              setError(null);
            }}
            className="w-full py-2.5 px-4 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-xl text-emerald-800 font-mono text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-2 shadow-2xs"
          >
            <Sparkles className="w-4 h-4 text-emerald-600 animate-pulse" />
            <span>{isUk ? "⚡ Спробувати на демо-прикладі в 1 клік (Конвертація Наказу ДСТУ в Word)" : "⚡ Try 1-click Demo Sample (Convert DSTU Order to Word)"}</span>
          </button>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Editor Area */}
            <div className="md:col-span-2 space-y-4">
              <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4 shadow-xs">
                {/* File Upload Zone */}
                <div className="flex items-center justify-between flex-wrap gap-2 border-b border-slate-100 pb-3">
                  <div className="flex items-center gap-2">
                    <FileCode className="w-4 h-4 text-indigo-600" />
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
                      {isUk ? "Вхідний файл або текст Markdown" : "Input File or Markdown Text"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileUpload}
                      accept=".md,.txt,.markdown"
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="py-1.5 px-3 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer"
                    >
                      <Upload className="w-3.5 h-3.5" />
                      <span>{isUk ? "Завантажити .md файл" : "Upload .md File"}</span>
                    </button>
                  </div>
                </div>

                {fileName && (
                  <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-xs">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-indigo-500" />
                      <span className="font-mono font-bold text-slate-800">{fileName}</span>
                      {fileSize && <span className="text-slate-400 font-mono">({Math.round(fileSize / 1024)} KB)</span>}
                    </div>
                    <button
                      onClick={() => {
                        setFileName(null);
                        setFileSize(null);
                        setMarkdownInput('');
                      }}
                      className="text-slate-400 hover:text-red-500 font-bold text-xs cursor-pointer"
                    >
                      ✕
                    </button>
                  </div>
                )}

                {/* Textarea */}
                <textarea
                  value={markdownInput}
                  onChange={(e) => setMarkdownInput(e.target.value)}
                  placeholder={isUk 
                    ? "Вставте текст у форматі Markdown (.md) або завантажте файл вище...\n\n# Заголовок наказу\n## Підзаголовок\n* Пункт 1\n* Пункт 2" 
                    : "Paste your Markdown (.md) text here or upload a file above..."}
                  rows={14}
                  className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/30 text-xs font-mono focus:outline-none transition-colors leading-relaxed placeholder-slate-400"
                />

                {/* Counter & Options */}
                <div className="flex items-center justify-between text-[11px] text-slate-500 font-mono">
                  <span>{isUk ? `Слів: ${markdownInput.trim() ? markdownInput.trim().split(/\s+/).length : 0} | Символів: ${markdownInput.length}` : `Words: ${markdownInput.trim() ? markdownInput.trim().split(/\s+/).length : 0} | Chars: ${markdownInput.length}`}</span>
                  
                  <label className="flex items-center gap-2 cursor-pointer text-slate-700 font-bold">
                    <input
                      type="checkbox"
                      checked={aiEnhance}
                      onChange={(e) => setAiEnhance(e.target.checked)}
                      className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500 cursor-pointer"
                    />
                    <span>{isUk ? "ШІ-покращення структури за ДСТУ" : "AI DSTU Layout Polish"}</span>
                  </label>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  onClick={handleDownloadWord}
                  disabled={!markdownInput.trim() || downloadingFormat !== null}
                  className="py-3 px-4 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-200 disabled:text-slate-400 text-white font-black text-xs uppercase tracking-wider rounded-xl flex items-center justify-center gap-2 transition-colors cursor-pointer shadow-xs"
                >
                  <Download className="w-4 h-4" />
                  <span>{isUk ? "Пряма конвертація в Word (.docx)" : "Direct Convert to Word (.docx)"}</span>
                </button>

                <button
                  onClick={handleRunConversion}
                  disabled={!markdownInput.trim()}
                  className="py-3 px-4 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-400 text-white font-black text-xs uppercase tracking-wider rounded-xl flex items-center justify-center gap-2 transition-colors cursor-pointer shadow-xs"
                >
                  <Eye className="w-4 h-4" />
                  <span>{isUk ? "Переглянути та сконвертувати" : "Preview & Convert"}</span>
                </button>
              </div>
            </div>

            {/* Sidebar Standard Info */}
            <div className="space-y-4">
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 space-y-4">
                <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5 border-b border-slate-200 pb-2">
                  <CheckCircle2 className="w-4 h-4 text-indigo-600" />
                  <span>{isUk ? "Параметри ДСТУ 4163:2020" : "DSTU 4163:2020 Specs"}</span>
                </h3>
                <ul className="space-y-2.5 text-[11px] text-slate-650 font-mono leading-relaxed">
                  <li className="flex items-start gap-2">
                    <span className="text-indigo-600 font-bold">•</span>
                    <span><strong>Шрифт:</strong> Times New Roman, 14 pt</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-indigo-600 font-bold">•</span>
                    <span><strong>Міжрядковий інтервал:</strong> 1.5 ліній</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-indigo-600 font-bold">•</span>
                    <span><strong>Поля сторінки:</strong> Ліве 30 мм, Праве 10 мм, Верхнє/Нижнє 20 мм</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-indigo-600 font-bold">•</span>
                    <span><strong>Абзацний відступ:</strong> 12.5 мм (1.5em)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-indigo-600 font-bold">•</span>
                    <span><strong>Таблиці & Схеми:</strong> Нативне відображення кордонів та заголовків</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading Processing State */}
      {isProcessing && (
        <div className="bg-white border border-slate-200 rounded-xl p-10 shadow-xs flex flex-col items-center justify-center min-h-[350px] space-y-6">
          <div className="relative w-16 h-16 flex items-center justify-center">
            <div className="absolute w-full h-full border-4 border-indigo-200 rounded-full animate-pulse" />
            <div className="absolute w-full h-full border-4 border-t-indigo-600 rounded-full animate-spin" />
            <FileType className="w-6 h-6 text-indigo-600" />
          </div>
          <div className="text-center space-y-2 max-w-md">
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-800">
              {isUk ? "Обробка документа ШІ..." : "Processing Document..."}
            </h3>
            <p className="text-xs text-slate-500 font-mono italic">
              {isUk ? "Форматування заголовків, таблиць та параметрів ДСТУ 4163:2020..." : "Applying DSTU 4163:2020 typography and margins..."}
            </p>
          </div>
        </div>
      )}

      {/* Error display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-5 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
          <div>
            <h4 className="text-xs font-bold text-red-800 uppercase tracking-wider">
              {isUk ? "Помилка конвертації" : "Conversion Error"}
            </h4>
            <p className="text-xs text-red-650 leading-relaxed font-mono mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Converted Preview & Download Controls */}
      {convertedMarkdown && !isProcessing && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-xs overflow-hidden space-y-0">
          {/* Top Toolbar */}
          <div className="bg-slate-800 text-white px-5 py-3.5 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="flex items-center bg-slate-700 p-1 rounded-lg">
                <button
                  onClick={() => setActiveTab('preview')}
                  className={`py-1 px-3 rounded text-xs font-bold font-mono transition-colors flex items-center gap-1.5 cursor-pointer ${activeTab === 'preview' ? 'bg-indigo-600 text-white' : 'text-slate-300 hover:text-white'}`}
                >
                  <Eye className="w-3.5 h-3.5" />
                  <span>{isUk ? "Прев'ю ДСТУ" : "DSTU Preview"}</span>
                </button>
                <button
                  onClick={() => setActiveTab('code')}
                  className={`py-1 px-3 rounded text-xs font-bold font-mono transition-colors flex items-center gap-1.5 cursor-pointer ${activeTab === 'code' ? 'bg-indigo-600 text-white' : 'text-slate-300 hover:text-white'}`}
                >
                  <Code className="w-3.5 h-3.5" />
                  <span>{isUk ? "Markdown Код" : "Markdown Code"}</span>
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handleCopy}
                className="py-1.5 px-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-xs font-bold font-mono flex items-center gap-1.5 cursor-pointer transition-colors"
              >
                {copiedCode ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                <span>{copiedCode ? (isUk ? 'Скопійовано' : 'Copied') : (isUk ? 'Копіювати' : 'Copy')}</span>
              </button>

              <button
                onClick={handleDownloadWord}
                disabled={downloadingFormat !== null}
                className="py-1.5 px-3.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-bold font-mono flex items-center gap-1.5 cursor-pointer transition-colors shadow-xs"
              >
                <Download className="w-3.5 h-3.5" />
                <span>{downloadingFormat === 'docx' ? (isUk ? 'Генерація...' : 'Generating...') : 'Word (.docx)'}</span>
              </button>

              <button
                onClick={handleDownloadPdf}
                disabled={downloadingFormat !== null}
                className="py-1.5 px-3.5 bg-rose-600 hover:bg-rose-500 text-white rounded-lg text-xs font-bold font-mono flex items-center gap-1.5 cursor-pointer transition-colors shadow-xs"
              >
                <Download className="w-3.5 h-3.5" />
                <span>{downloadingFormat === 'pdf' ? (isUk ? 'Генерація...' : 'Generating...') : 'PDF (.pdf)'}</span>
              </button>
            </div>
          </div>

          {/* Document Content */}
          {activeTab === 'preview' ? (
            <div className="p-8 sm:p-12 bg-white min-h-[500px]">
              {/* Paper Sheet Simulator with DSTU Margins */}
              <div className="max-w-[700px] mx-auto bg-white border border-slate-200 shadow-md p-8 sm:p-12 font-serif text-slate-800 leading-relaxed space-y-2">
                {renderDstuPreview(convertedMarkdown)}
              </div>
            </div>
          ) : (
            <div className="p-6 bg-slate-900 text-slate-200 font-mono text-xs overflow-x-auto whitespace-pre-wrap leading-relaxed border-t border-slate-800 max-h-[600px]">
              {convertedMarkdown}
            </div>
          )}

          {/* Footer Reset */}
          <div className="p-4 bg-slate-50 border-t border-slate-200 flex justify-between items-center">
            <span className="text-[11px] font-mono text-slate-500">
              {isUk ? "Стандарт: ДСТУ 4163:2020 | Шрифти: Times New Roman 14pt" : "Standard: DSTU 4163:2020 | Font: Times New Roman 14pt"}
            </span>
            <button
              onClick={() => {
                setConvertedMarkdown(null);
                setMarkdownInput('');
                setFileName(null);
              }}
              className="py-1.5 px-4 bg-white hover:bg-slate-100 border border-slate-300 text-slate-700 font-bold font-mono text-xs rounded-lg transition-colors cursor-pointer"
            >
              {isUk ? "Нова конвертація" : "New Conversion"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
